"use client";

import { useSyncExternalStore } from "react";
import { Button } from "@/components/ui/button";

const THEMES = [
  { value: "initial", label: "Initial" },
  { value: "comfortable", label: "Comfortable" },
  { value: "contrast", label: "Contrast" },
  { value: "branded", label: "Branded" },
] as const;

type ThemeName = (typeof THEMES)[number]["value"];

const STORAGE_KEY = "skylight-theme";
// localStorage's own "storage" event only fires in *other* tabs, not the
// one that made the change — this custom event covers the current tab.
const THEME_CHANGE_EVENT = "skylight-theme-change";

function isThemeName(value: string | null): value is ThemeName {
  return THEMES.some((t) => t.value === value);
}

function subscribe(onChange: () => void) {
  window.addEventListener("storage", onChange);
  window.addEventListener(THEME_CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(THEME_CHANGE_EVENT, onChange);
  };
}

function getSnapshot(): ThemeName {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isThemeName(stored) ? stored : "initial";
}

function getServerSnapshot(): ThemeName {
  return "initial";
}

function applyTheme(theme: ThemeName) {
  document.documentElement.setAttribute("data-theme", theme);
  window.localStorage.setItem(STORAGE_KEY, theme);
  window.dispatchEvent(new Event(THEME_CHANGE_EVENT));
}

/**
 * Temporary A/B testing widget — lets anyone flip between the shipped
 * design ("Initial") and two alternative token sets without a rebuild.
 * Remove once a direction is picked; see globals.css for the theme
 * definitions this switches between.
 */
export function ThemeSwitcher() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center gap-1 rounded-lg border bg-card p-1 shadow-lg">
      <span className="px-2 text-xs text-muted-foreground">Theme</span>
      {THEMES.map((t) => (
        <Button
          key={t.value}
          type="button"
          size="sm"
          variant={theme === t.value ? "default" : "ghost"}
          onClick={() => applyTheme(t.value)}
        >
          {t.label}
        </Button>
      ))}
    </div>
  );
}
